import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, RouterProvider, useParams, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Layouts, guards and error pages are kept eager: they are small, render on
// (almost) every route and gate lazy children, so splitting them adds requests
// without meaningful payload savings.
import BharatConnectLayout from '@/features/bharat-connect/components/BharatConnectLayout';
import RootLayout from '@/components/layouts/RootLayout';
import MainLayout from '@/components/layouts/MainLayout';
import ProductLayout from '@/components/layouts/ProductLayout';
import MobileProductLayout from '@/components/layouts/MobileProductLayout';
import RouteErrorPage from '@/components/RouteErrorPage';
import NotFoundPage from '@/components/NotFoundPage';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { products } from '@/services/mock-data';
import { Navigate } from 'react-router-dom';
import PageLoader from '@/components/PageLoader';
import OnboardingAppLayout from '@/onboarding-demos-src/components/AppLayout';
import ScrollToTop from '@/onboarding-demos-src/components/shared/ScrollToTop';

// Heavy feature pages are lazily loaded so each demo ships as its own chunk and
// visitors only download the routes they actually open.
const HomePage = lazy(() => import('@/features/homepage/HomePage'));
const ProductDemoPage = lazy(() => import('@/features/product/ProductDemoPage'));
const Login = lazy(() => import('@/features/auth/Login'));
const PartnerRegistration = lazy(() => import('@/features/partner-registration/PartnerRegistration'));
const OurProducts = lazy(() => import('@/features/partner-registration/OurProducts'));
const AdminDashboard = lazy(() => import('@/features/admin/AdminDashboard'));
const AdminUsersPage = lazy(() => import('@/features/admin/pages/AdminUsersPage'));
const AdminProductsPage = lazy(() => import('@/features/admin/pages/AdminProductsPage'));
const AppointmentsSection = lazy(() =>
    import('@/features/admin/components/AppointmentsSection').then(m => ({ default: m.AppointmentsSection }))
);
const EnquiriesSection = lazy(() =>
    import('@/features/admin/components/EnquiriesSection').then(m => ({ default: m.EnquiriesSection }))
);
const BanlDemoPage = lazy(() => import('@/features/banl-demo/BanlDemoPage'));
const ImpsDemoPage = lazy(() => import('@/features/imps-demo/ImpsDemoPage'));
const UpiSwitchDashboard = lazy(() => import('@/features/upi-switch/pages/UpiSwitchDashboard'));
const UpiAcquiringPage = lazy(() => import('@/features/upi-switch/pages/UpiAcquiringPage'));
const UpiIssuingPage = lazy(() => import('@/features/upi-switch/pages/UpiIssuingPage'));
const UpiCreditLinePage = lazy(() => import('@/features/upi-switch/pages/UpiCreditLinePage'));
const EkycDemoPage = lazy(() => import('@/features/ekyc-setu/pages/EkycDemoPage'));
const UnifiedSwitchStoryPage = lazy(() => import('@/features/unified-story/pages/UnifiedSwitchStoryPage'));
const StyleGuide = lazy(() => import('@/pages/StyleGuide'));
const OnboardingDemosHome = lazy(() => import('@/onboarding-demos-src/pages/Home'));
const BankDemoWizard = lazy(() => import('@/onboarding-demos-src/features/onboarding/bank/pages/bank-demo-wizard'));
const CBSDemoWizard = lazy(() => import('@/onboarding-demos-src/features/onboarding/cbs/pages/cbs-demo-wizard'));
const AppointmentBookingWizard = lazy(() => import('@/modules/appointment-booking/pages/AppointmentBookingWizard'));

// Wrap a lazily-loaded element in a Suspense boundary with the shared loader.
const el = (node: ReactNode): ReactNode => <Suspense fallback={<PageLoader />}>{node}</Suspense>;

const onboardingQueryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            staleTime: Infinity,
        },
    },
});

function OnboardingDemosWrapper() {
    return (
        <QueryClientProvider client={onboardingQueryClient}>
            <ScrollToTop />
            <OnboardingAppLayout>
                <Suspense fallback={<PageLoader />}>
                    <Outlet />
                </Suspense>
            </OnboardingAppLayout>
        </QueryClientProvider>
    );
}

function ModuleGuardWrapper() {
    const { productSlug } = useParams();
    return <AuthGuard requiredPermission={productSlug} />;
}

function ProductLayoutWrapper() {
    const { productSlug } = useParams();
    const product = products.find(p => p.slug === productSlug);

    if (
        productSlug === 'banking-app' ||
        productSlug === 'merchant-onboarding' ||
        productSlug === 'cbs-mobile' ||
        productSlug === 'los-lms' ||
        productSlug?.startsWith('bbps-') ||
        productSlug === 'bharat-connect' ||
        productSlug === 'bharat-connect-ecosystem' ||
        productSlug === 'bharat-connect-biller-portal' ||
        productSlug === 'bharatlinc' ||
        productSlug === 'bharatlinc-bou' ||
        productSlug === 'bharat-connect-bou-dashboard'
    ) {
        return <Outlet />;
    }

    if (productSlug?.startsWith('bharat-connect-')) {
        return <BharatConnectLayout />;
    }


    if (product?.type === 'mobile') {
        return <MobileProductLayout />;
    }

    return <ProductLayout />;
}

const router = createBrowserRouter([
    {
        path: '/login',
        element: el(<Login />),
    },
    {
        path: '/bank-enquiry',
        element: el(<PartnerRegistration />),
    },
    {
        path: '/our-products',
        element: el(<OurProducts />),
    },
    {
        path: '/book-appointment',
        element: <RootLayout />,
        children: [
            {
                index: true,
                element: el(<AppointmentBookingWizard />),
            }
        ]
    },
    {
        path: '/',
        element: <AuthGuard />,
        errorElement: <RouteErrorPage />,
        children: [
            {
                path: 'style-guide',
                element: <AuthGuard adminOnly />,
                children: [{ index: true, element: el(<StyleGuide />) }]
            },
            {
                element: <RootLayout />,
                children: [
                    {
                        path: 'xpresslinc',
                        element: <AuthGuard requiredPermission="xpresslinc" />,
                        children: [
                            {
                                index: true,
                                element: el(<UnifiedSwitchStoryPage />),
                            },
                            {
                                path: 'ekyc-aeps',
                                element: el(<EkycDemoPage />),
                            },
                            {
                                path: 'banl-switch',
                                element: el(<BanlDemoPage />),
                            },
                            {
                                path: 'imps-switch',
                                element: el(<ImpsDemoPage />),
                            },
                            {
                                path: 'upi-switch',
                                element: <Outlet />,
                                children: [
                                    {
                                        index: true,
                                        element: el(<UpiSwitchDashboard />),
                                    },
                                    {
                                        path: 'acquiring',
                                        element: el(<UpiAcquiringPage />),
                                    },
                                    {
                                        path: 'issuing',
                                        element: el(<UpiIssuingPage />),
                                    },
                                    {
                                        path: 'credit-line',
                                        element: el(<UpiCreditLinePage />),
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        element: <MainLayout />,
                        children: [
                            {
                                index: true,
                                element: el(<HomePage />),
                            },
                        ],
                    },
                    {
                        path: 'admin',
                        element: <AuthGuard adminOnly />,
                        children: [
                            {
                                element: el(<AdminDashboard />),
                                children: [
                                    { index: true,            element: <Navigate to="users" replace /> },
                                    { path: 'users',          element: el(<AdminUsersPage />) },
                                    { path: 'products',       element: el(<AdminProductsPage />) },
                                    { path: 'enquiries',      element: el(<EnquiriesSection />) },
                                    { path: 'appointments',   element: el(<AppointmentsSection />) },
                                ]
                            }
                        ]
                    },
                    {
                        path: 'onboarding-demos',
                        children: [
                            {
                                element: <OnboardingDemosWrapper />,
                                children: [
                                    {
                                        index: true,
                                        element: el(<OnboardingDemosHome />),
                                    },
                                    {
                                        path: 'bank-demo',
                                        element: <AuthGuard requiredPermission="bank-demo" />,
                                        children: [{ index: true, element: el(<BankDemoWizard />) }]
                                    },
                                    {
                                        path: 'cbs-demo',
                                        element: <AuthGuard requiredPermission="cbs-demo" />,
                                        children: [{ index: true, element: el(<CBSDemoWizard />) }]
                                    },
                                ],
                            }
                        ]
                    },
                    {
                        path: 'product/:productSlug',
                        element: <ProductLayoutWrapper />,
                        children: [
                            {
                                element: <ModuleGuardWrapper />,
                                children: [
                                    {
                                        index: true,
                                        element: el(<ProductDemoPage />),
                                    },
                                ]
                            }
                        ],
                    },
                ],
            }
        ],
    },
    {
        // Catch-all 404 route
        path: '*',
        element: <NotFoundPage />,
    },
]);

export function AppRouter() {
    return <RouterProvider router={router} />;
}
